import type { ContextDto } from '@inference.engine/api-client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@monksflow/monks-ui';
import { AnimatedTableRow } from '@/shared/ui/animations';
import { PaginationControls } from '@/shared/ui';
import { formatRelativeDate } from '@/shared/utils/dateUtils';
import { SessionsTitleCell } from '../SessionsContent/SessionsTitleCell/SessionsTitleCell';
import { useTranslation } from 'react-i18next';

interface SessionsTableProps {
  rows: ContextDto[];
  isLoading: boolean;
  totalCount: number;
  page: number;
  rowsPerPage: number;
  onRowClick: (sessionId: string) => void;
  onPageChange: (page: number) => void;
}

/**
 * Sessions data table matching the Beat UI design.
 */
export function SessionsTable({
  rows,
  isLoading,
  totalCount,
  page,
  rowsPerPage,
  onRowClick,
  onPageChange,
}: SessionsTableProps) {
  const { t } = useTranslation('chats');

  return (
    <div className="grid h-full min-h-0 w-full grid-rows-[minmax(0,1fr)_auto]">
      <div className="min-h-0 overflow-auto px-section">
        <Table>
          <TableHeader>
            <TableRow className="border-b-2! border-black/19">
              <TableHead className="w-[70%] type-label-sm text-muted-foreground">
                {t('sessions.sessionsTable.title')}
              </TableHead>
              <TableHead className="w-[15%] type-label-sm text-muted-foreground">
                {t('sessions.sessionsTable.createdAt')}
              </TableHead>
              <TableHead className="w-[15%] type-label-sm text-muted-foreground">
                {t('sessions.sessionsTable.lastUpdated')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="h-full text-center text-muted-foreground">
                  {t('sessions.sessionsTable.loading')}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="h-full text-center text-muted-foreground">
                  {t('sessions.sessionsTable.noSessionsFound')}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <AnimatedTableRow
                  key={row.id}
                  index={index}
                  className="h-[72px] cursor-pointer"
                  onClick={() => onRowClick(row.id)}
                >
                  <SessionsTitleCell contextId={row.id} title={row.title ?? ''} />

                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {formatRelativeDate(row.createdAt)}
                    </span>
                  </TableCell>

                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {formatRelativeDate(row.updatedAt)}
                    </span>
                  </TableCell>
                </AnimatedTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationControls
        page={page + 1}
        totalItems={totalCount}
        pageSize={rowsPerPage}
        onPageChange={(nextPage) => onPageChange(nextPage - 1)}
        className="mt-auto p-section"
      />
    </div>
  );
}
